"use client"

/**
 * Library secondary sidebar — All / My / folder tree (Font Awesome only).
 * Scope syncs to the main hub via `?scope=` + optional `folderId=` (`lib/library-nav.ts`).
 */

import * as React from "react"
import { Link } from "react-router"
import { useLocation, useNavigate, useSearchParams } from "react-router"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Shortcut,
} from "@/components/ui/dropdown-menu"
import { Kbd, KbdGroup } from "@/components/ui/kbd"
import { Tip } from "@/components/ui/tip"
import { cn } from "@/lib/utils"
import type { LibraryFolder } from "@/lib/mock/library-folders"
import { LIBRARY_FOLDERS } from "@/lib/mock/library"
import {
  newFolderId,
  collectFolderDescendantIds,
  isValidFolderMove,
  libraryFolderMoveTargets,
} from "@/lib/mock/library-folders"
import { useSecondaryPanel } from "@/components/sidebar"
import {
  SecondaryHubIconNavRow,
  SecondaryHubNavCompactShell,
  SecondaryHubNavRow,
  SecondaryHubNavSectionHeader,
  useSecondaryHubNavChrome,
} from "@/components/sidebar"
import { LibraryNewFolderSheet } from "@/components/library-new-folder-sheet"
import {
  isLibraryNavActive,
  parseLibraryNav,
  coerceLibraryNav,
  libraryNavStatesEqual,
  LIBRARY_FAVORITES_FOLDER_ID,
  libraryFavoritesFolderHref,
  libraryHubScopeHref,
  libraryImportedHref,
} from "@/lib/library-nav"
import { useLibrarySessionNewImportIds } from "@/lib/use-library-session-new-import-ids"
import { useQuestionImportDocs, runningQuestionImport, latestQuestionImportBanner } from "@/hooks/use-question-import"
import { libraryFolderMatchesSearch } from "@/components/data-views/library-folder-tree-branch-shared"
import { LibraryFolderTreeBranch } from "@/components/data-views/library-folder-tree-branch"
import { outlineTreeBranchDepthStyle } from "@/components/data-views/outline-tree-menu"
import { LibraryFolderSearchInput } from "@/components/library-folder-picker-panel"
import { SidebarCollapsedSearchButton } from "@/components/sidebar/sidebar-collapsed-search-button"
import { useProduct } from "@/contexts/product-context"
import { productPersistKey } from "@/stores/app-store"
import { usePersistedState } from "@exxatdesignux/ui/lib/persisted-state"

export function LibrarySecondaryNav() {
  const pathname = useLocation().pathname
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const searchParamsKey = searchParams.toString()
  const { product, customProducts, activeCustomIndex } = useProduct()
  const pinnedPersistKey = productPersistKey(
    product,
    "library:pinned-folders:v1",
    customProducts,
    activeCustomIndex,
  )
  const [pinnedFolderIds, setPinnedFolderIds] = usePersistedState<string[]>(
    pinnedPersistKey,
    [],
  )
  const { openPanel, libraryFolderBridge, libraryAccessBridge } = useSecondaryPanel()
  const { showCompactRail, dismissNavFlyout } = useSecondaryHubNavChrome()
  const [newFolderOpen, setNewFolderOpen] = React.useState(false)
  const [newFolderParentId, setNewFolderParentId] = React.useState<string | null>(null)
  const [customizingFolder, setCustomizingFolder] = React.useState<LibraryFolder | null>(null)
  const [deleteFolder, setDeleteFolder] = React.useState<LibraryFolder | null>(null)
  const [moveFolder, setMoveFolder] = React.useState<LibraryFolder | null>(null)
  const [folderSearchOpen, setFolderSearchOpen] = React.useState(false)
  const [folderSearchQuery, setFolderSearchQuery] = React.useState("")
  const folderSearchInputRef = React.useRef<HTMLInputElement>(null)
  const folders = libraryFolderBridge?.folders ?? LIBRARY_FOLDERS
  const folderItemCounts = libraryFolderBridge?.folderItemCounts ?? {}
  const scopeCounts = libraryFolderBridge?.scopeCounts ?? {
    all: 0,
    my: 0,
    favorites: 0,
    imported: 0,
  }
  const importDocs = useQuestionImportDocs()
  const importRunning = runningQuestionImport(importDocs, "bank") !== null
  const sessionNewImportIds = useLibrarySessionNewImportIds(importDocs)
  const importOutcomeVisible = React.useMemo(() => {
    if (importRunning) return null
    return latestQuestionImportBanner(importDocs, "bank")
  }, [importDocs, importRunning])
  const importedIsNew =
    importRunning ||
    sessionNewImportIds.size > 0 ||
    (importOutcomeVisible != null &&
      (importOutcomeVisible.status === "done" ||
        importOutcomeVisible.status === "imported"))
  const nav = React.useMemo(() => {
    const parsed = parseLibraryNav(new URLSearchParams(searchParamsKey))
    return coerceLibraryNav(parsed, folders)
  }, [searchParamsKey, folders])

  React.useEffect(() => {
    const parsed = parseLibraryNav(new URLSearchParams(searchParamsKey))
    const coerced = coerceLibraryNav(parsed, folders)
    if (libraryNavStatesEqual(parsed, coerced)) return
    navigate(
      libraryHubScopeHref(pathname, searchParams, {
        scope: coerced.scope,
        folderId: coerced.folderId,
      }),
      { replace: true },
    )
  }, [folders, navigate, pathname, searchParams, searchParamsKey])

  const folderTreeScopeActive =
    nav.scope === "folder" &&
    nav.folderId != null &&
    nav.folderId !== LIBRARY_FAVORITES_FOLDER_ID

  const canManageFolders = libraryFolderBridge != null
  const canManageAccess = libraryAccessBridge != null

  /** Favorites is a primary nav row (with All / My), not under “Folders”. */
  const folderTreeRoots = React.useMemo(() => {
    const roots = folders
      .filter(f => f.parentId === null && f.id !== LIBRARY_FAVORITES_FOLDER_ID)
    const pinned = new Set(pinnedFolderIds)
    return roots.sort((a, b) => {
      const aPinned = pinned.has(a.id)
      const bPinned = pinned.has(b.id)
      if (aPinned !== bPinned) return aPinned ? -1 : 1
      return a.name.localeCompare(b.name)
    })
  }, [folders, pinnedFolderIds])

  const expandForFolderSearch = React.useCallback(() => {
    setFolderSearchOpen(true)
    openPanel("library")
  }, [openPanel])

  React.useEffect(() => {
    if (!folderSearchOpen) return
    window.requestAnimationFrame(() => folderSearchInputRef.current?.focus())
  }, [folderSearchOpen])

  const folderSearchActive = folderSearchQuery.trim().length > 0
  const hasVisibleFolderMatches = React.useMemo(
    () =>
      !folderSearchActive ||
      folderTreeRoots.some(root =>
        libraryFolderMatchesSearch(root, folders, folderSearchQuery),
      ),
    [folderSearchActive, folderSearchQuery, folderTreeRoots, folders],
  )

  const togglePinFolder = React.useCallback(
    (folder: LibraryFolder) => {
      setPinnedFolderIds(current =>
        current.includes(folder.id)
          ? current.filter(id => id !== folder.id)
          : [...current, folder.id],
      )
    },
    [setPinnedFolderIds],
  )

  const openMoveFolder = React.useCallback((folder: LibraryFolder) => {
    setMoveFolder(folder)
  }, [])

  const commitMoveFolder = React.useCallback(
    (targetParentId: string | null) => {
      if (!moveFolder || !libraryFolderBridge) return
      if (!isValidFolderMove(folders, moveFolder.id, targetParentId)) return
      libraryFolderBridge.onFoldersChange(prev =>
        prev.map(f =>
          f.id === moveFolder.id ? { ...f, parentId: targetParentId } : f,
        ),
      )
      setMoveFolder(null)
    },
    [folders, libraryFolderBridge, moveFolder],
  )

  const openTopLevelFolder = React.useCallback(() => {
    setCustomizingFolder(null)
    setNewFolderParentId(nav.scope === "folder" ? nav.folderId : null)
    setNewFolderOpen(true)
  }, [nav.folderId, nav.scope])

  const openSubfolder = React.useCallback((parentId: string) => {
    setCustomizingFolder(null)
    setNewFolderParentId(parentId)
    setNewFolderOpen(true)
  }, [])

  const openCustomizeFolder = React.useCallback((folder: LibraryFolder) => {
    setCustomizingFolder(folder)
    setNewFolderParentId(folder.parentId)
    setNewFolderOpen(true)
  }, [])

  const openManageAccess = React.useCallback(() => {
    libraryAccessBridge?.openManageAccess()
  }, [libraryAccessBridge])

  const openDeleteFolder = React.useCallback((folder: LibraryFolder) => {
    setDeleteFolder(folder)
  }, [])

  const commitDeleteFolder = React.useCallback(() => {
    if (!deleteFolder || !libraryFolderBridge) return
    const victim = deleteFolder
    const parent = victim.parentId
    const desc = collectFolderDescendantIds(folders, victim.id)
    const remaining = folders.filter(f => !desc.has(f.id))
    if (remaining.length === 0) {
      setDeleteFolder(null)
      return
    }
    const parentStillExists = parent !== null && remaining.some(f => f.id === parent)
    const fallbackRoot = remaining.find(f => f.parentId === null)?.id
    const reassignTarget =
      parentStillExists ? parent : (fallbackRoot ?? remaining[0]!.id)

    libraryFolderBridge.onFoldersChange(remaining)
    libraryFolderBridge.onItemsChange(prev =>
      prev.map(item => (desc.has(item.folderId) ? { ...item, folderId: reassignTarget } : item)),
    )

    if (nav.scope === "folder" && nav.folderId && desc.has(nav.folderId)) {
      navigate(
        libraryHubScopeHref(
          pathname,
          new URLSearchParams(searchParamsKey),
          parentStillExists
            ? { scope: "folder", folderId: parent! }
            : { scope: "all" },
        ),
        { replace: true },
      )
    }

    setDeleteFolder(null)
  }, [deleteFolder, folders, nav.folderId, nav.scope, pathname, libraryFolderBridge, navigate, searchParamsKey])

  const sheetParentId = customizingFolder?.parentId ?? newFolderParentId

  const flattenedFolderLinks = React.useMemo(() => {
    const out: LibraryFolder[] = []
    const walk = (folder: LibraryFolder) => {
      out.push(folder)
      folders
        .filter(c => c.parentId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach(walk)
    }
    folderTreeRoots.forEach(walk)
    return out
  }, [folderTreeRoots, folders])

  const hubNavModals = (
    <>
      <LibraryNewFolderSheet
        open={newFolderOpen}
        onOpenChange={open => {
          setNewFolderOpen(open)
          if (!open) {
            setCustomizingFolder(null)
            setNewFolderParentId(null)
          }
        }}
        parentFolderId={sheetParentId}
        customizingFolder={customizingFolder}
        descriptionText={
          customizingFolder
            ? "Update the folder name, color, and icon shown in the navigation and folder views."
            : sheetParentId
              ? "The folder is created inside the folder selected in the navigation."
              : "Add a top-level folder to the library."
        }
        onCreated={folder => {
          if (customizingFolder) {
            libraryFolderBridge?.onFoldersChange(prev =>
              prev.map(item =>
                item.id === customizingFolder.id
                  ? {
                      ...item,
                      name: folder.name,
                      icon: folder.icon,
                      colorKey: folder.colorKey,
                    }
                  : item,
              ),
            )
          } else {
            libraryFolderBridge?.onFoldersChange(prev => [...prev, { ...folder, id: newFolderId() }])
          }
          setNewFolderOpen(false)
          setCustomizingFolder(null)
          setNewFolderParentId(null)
        }}
      />
      {deleteFolder ? <Shortcut keys="Enter" onInvoke={commitDeleteFolder} /> : null}
      <Dialog open={deleteFolder != null} onOpenChange={open => !open && setDeleteFolder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete folder?</DialogTitle>
            <DialogDescription>
              {deleteFolder
                ? `${deleteFolder.name} and its subfolders will be removed. Questions inside move to the parent folder (or the first top-level folder).`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" size="sm" onClick={() => setDeleteFolder(null)}>
              Cancel
              <KbdGroup className="ms-1.5">
                <Kbd variant="bare">Esc</Kbd>
              </KbdGroup>
            </Button>
            <Button type="button" variant="destructive" size="sm" onClick={commitDeleteFolder}>
              Delete
              <KbdGroup className="ms-1.5">
                <Kbd variant="bare">⏎</Kbd>
              </KbdGroup>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={moveFolder != null} onOpenChange={open => !open && setMoveFolder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move to subfolder</DialogTitle>
            <DialogDescription>
              Choose a new parent. Subfolders stay with this folder.
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-64 flex-col gap-1 overflow-y-auto py-2">
            {moveFolder
              ? libraryFolderMoveTargets(folders, moveFolder.id).map(target => (
                  <Button
                    key={target.id ?? "root"}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="justify-start font-normal"
                    onClick={() => commitMoveFolder(target.id)}
                  >
                    {target.label}
                  </Button>
                ))
              : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setMoveFolder(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

  if (showCompactRail) {
    return (
      <>
        <SecondaryHubNavCompactShell
          ariaLabel="Question Bank"
          panelId="library"
          footer={
            <div className="flex flex-col items-center gap-1 border-t border-sidebar-border/60 px-1 py-2">
              <SidebarCollapsedSearchButton
                label="Search folders"
                onActivate={expandForFolderSearch}
              />
              {canManageFolders ? (
                <Tip label="Add folder" side="right">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="icon-button-chrome size-9 shrink-0"
                    aria-label="Add folder"
                    onClick={openTopLevelFolder}
                  >
                    <i className="fa-light fa-plus text-md" aria-hidden="true" />
                  </Button>
                </Tip>
              ) : null}
            </div>
          }
        >
          <SecondaryHubIconNavRow
            href={libraryHubScopeHref(pathname, searchParams, { scope: "all" })}
            active={isLibraryNavActive(pathname, nav, "all", null, folders)}
            iconClass="fa-table-list"
            label="All questions"
            onClick={() => openPanel("library")}
          />
          <SecondaryHubIconNavRow
            href={libraryHubScopeHref(pathname, searchParams, { scope: "my" })}
            active={isLibraryNavActive(pathname, nav, "my", null, folders)}
            iconClass="fa-user"
            label="My questions"
            onClick={() => openPanel("library")}
          />
          <SecondaryHubIconNavRow
            href={libraryFavoritesFolderHref(pathname, searchParams)}
            active={isLibraryNavActive(pathname, nav, "folder", LIBRARY_FAVORITES_FOLDER_ID, folders)}
            iconClass="fa-star"
            label="Favorites"
            onClick={() => openPanel("library")}
          />
          <SecondaryHubIconNavRow
            href={libraryImportedHref(pathname, searchParams)}
            active={isLibraryNavActive(pathname, nav, "import", null, folders)}
            iconClass="fa-file-import"
            busy={importRunning}
            isNew={importedIsNew}
            label="Imported questions"
            onClick={() => openPanel("library")}
          />
          <li className="flex w-full justify-center pt-1" role="none">
            <DropdownMenu>
              <Tip label="Folders" side="right">
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className={cn(
                      "size-9 shrink-0 text-sidebar-foreground",
                      folderTreeScopeActive &&
                        "bg-sidebar-accent text-sidebar-accent-foreground",
                    )}
                    aria-current={folderTreeScopeActive ? "page" : undefined}
                    aria-label="Folders"
                  >
                    <i className="fa-light fa-folder-tree text-md" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </Tip>
              <DropdownMenuContent side="right" align="start" className="max-h-72 overflow-y-auto">
                {flattenedFolderLinks.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">No folders</div>
                ) : (
                  flattenedFolderLinks.map(folder => {
                    const href = libraryHubScopeHref(pathname, searchParams, {
                      scope: "folder",
                      folderId: folder.id,
                    })
                    const active = isLibraryNavActive(pathname, nav, "folder", folder.id, folders)
                    return (
                      <DropdownMenuItem key={folder.id} asChild>
                        <Link
                          to={href}
                          className={cn(active && "bg-accent")}
                          onClick={() => {
                            openPanel("library")
                            dismissNavFlyout()
                          }}
                        >
                          {folder.name}
                        </Link>
                      </DropdownMenuItem>
                    )
                  })
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        </SecondaryHubNavCompactShell>
        {hubNavModals}
      </>
    )
  }

  return (
    <>
      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-4" aria-label="Question Bank">
        <ul className="space-y-0.5">
          <SecondaryHubNavRow
            href={libraryHubScopeHref(pathname, searchParams, { scope: "all" })}
            active={isLibraryNavActive(pathname, nav, "all", null, folders)}
            iconClass="fa-table-list"
            label="All questions"
            count={scopeCounts.all}
            onClick={() => openPanel("library")}
          />
          <SecondaryHubNavRow
            href={libraryHubScopeHref(pathname, searchParams, { scope: "my" })}
            active={isLibraryNavActive(pathname, nav, "my", null, folders)}
            iconClass="fa-user"
            label="My questions"
            count={scopeCounts.my}
          />
          <SecondaryHubNavRow
            href={libraryFavoritesFolderHref(pathname, searchParams)}
            active={isLibraryNavActive(pathname, nav, "folder", LIBRARY_FAVORITES_FOLDER_ID, folders)}
            iconClass="fa-star"
            label="Favorites"
            count={scopeCounts.favorites}
            onClick={() => openPanel("library")}
          />
          <SecondaryHubNavRow
            href={libraryImportedHref(pathname, searchParams)}
            active={isLibraryNavActive(pathname, nav, "import", null, folders)}
            iconClass="fa-file-import"
            busy={importRunning}
            isNew={importedIsNew}
            label="Imported questions"
            count={scopeCounts.imported}
            onClick={() => openPanel("library")}
          />
          <SecondaryHubNavSectionHeader
            label="Folders"
            action={
              <div className="flex items-center gap-0.5">
                <Tip label="Search folders" side="right">
                  <Button
                    type="button"
                    size="icon-xs"
                    variant="ghost"
                    className={cn(
                      "icon-button-chrome shrink-0",
                      folderSearchOpen &&
                        "bg-sidebar-accent text-sidebar-accent-foreground",
                    )}
                    aria-label="Search folders"
                    aria-pressed={folderSearchOpen}
                    onClick={() => setFolderSearchOpen(open => !open)}
                  >
                    <i className="fa-light fa-magnifying-glass" aria-hidden="true" />
                  </Button>
                </Tip>
                <Tip label="Add folder" side="right">
                  <Button
                    type="button"
                    size="icon-xs"
                    variant="ghost"
                    className="icon-button-chrome shrink-0"
                    aria-label="Add folder"
                    disabled={!canManageFolders}
                    onClick={openTopLevelFolder}
                  >
                    <i className="fa-light fa-plus" aria-hidden="true" />
                  </Button>
                </Tip>
              </div>
            }
          />
          {folderSearchOpen ? (
            <li className="px-2 pb-1">
              <LibraryFolderSearchInput
                value={folderSearchQuery}
                onChange={setFolderSearchQuery}
                inputRef={folderSearchInputRef}
              />
            </li>
          ) : null}
          {folderSearchActive && !hasVisibleFolderMatches ? (
            <li className="px-3 py-1.5 text-xs text-muted-foreground">
              No folders match that search.
            </li>
          ) : null}
          {folderTreeRoots.map(folder => (
            <li
              key={folder.id}
              className="min-w-0 w-full list-none py-0"
              style={outlineTreeBranchDepthStyle(0)}
            >
              <LibraryFolderTreeBranch
                folder={folder}
                folders={folders}
                folderItemCounts={folderItemCounts}
                pathname={pathname}
                hubSearchParams={searchParams}
                nav={nav}
                canManageFolders={canManageFolders}
                canManageAccess={canManageAccess}
                onAddSubfolder={openSubfolder}
                onCustomizeFolder={openCustomizeFolder}
                onManageAccess={openManageAccess}
                onDeleteFolder={openDeleteFolder}
                onPinToTop={togglePinFolder}
                onRenameFolder={openCustomizeFolder}
                onMoveFolder={openMoveFolder}
                pinnedFolderIds={pinnedFolderIds}
                searchQuery={folderSearchActive ? folderSearchQuery : undefined}
              />
            </li>
          ))}
        </ul>
      </nav>
      {hubNavModals}
    </>
  )
}
